/**
 * Copy for the shared vendor-payments components under `components/VendorPayments` — the nested
 * payments table, its card-list equivalent and the documentation drawer.
 *
 * Its own namespace rather than one flow's: the create-order and create-claim Confirm steps both
 * render these, and rooting the copy in `orders.createOrderRework` would have the claim flow reading
 * order copy. Wording that names the document being built — what the top-level request is called,
 * the column that holds a deposit's or retention's share, and the two tooltips that explain them —
 * is deliberately *absent* here and passed in by the flow. Everything below describes a payment or
 * the control itself and reads the same either way.
 */
export default {
  paymentCount: '1 payment | {count} payments',
  attachments: 'Documentation for {vendor}',
  /** The card list's own labels: a card has no column headers to read them off. */
  vendorTotal: 'Vendor total',
  /** Heads the deduction breakdown: what the vendor claimed, before anything came off it. */
  claimed: 'Claimed',
  /** The expander names the rows it reveals rather than the gesture. */
  showPayments: 'Show payments for {vendor}',
  hidePayments: 'Hide payments for {vendor}',
  /**
   * What a sub-payment is called. The top-level request names the document, so the flow passes that
   * one in — these two are the same word either way.
   */
  requestType: {
    deposit: 'Deposit',
    retention: 'Retention',
    valueContraCharge: 'Value contra charge',
    itemContraCharge: 'Item contra charge',
  },
  /** The dropdown trigger, where a flow offers more than one kind of addition. */
  addAction: 'Add',
  addDeposit: 'Add deposit',
  removeDeposit: 'Remove deposit',
  addRetention: 'Add retention',
  removeRetention: 'Remove retention',
  addValueContraCharge: 'Add value contra charge',
  removeValueContraCharge: 'Remove contra charge',
  addItemContraCharge: 'Add item contra charge',
  removeItemContraCharge: 'Remove item contra charge',

  /**
   * What each addition does, under its name in the menu.
   *
   * The four labels name four things a reader has to tell apart — two of them differ only by the
   * word "value" or "item" — so the label alone leaves the choice to be guessed at. Said in the
   * neutral terms this namespace is for: "the request" rather than the order or the claim, since
   * both flows read these.
   */
  additionDescriptions: {
    deposit: 'Pay part of the request up front, split off as its own payment',
    depositAdded: 'Already added — a request takes one deposit',
    retention: 'Hold back a percentage until the work is signed off',
    valueContraCharge: 'Deduct a flat amount from what this vendor is paid',
    itemContraCharge: 'Charge back a BOQ line at a quantity and a rate',
  },
  /** Reopens the dialog the charge was set up in — the row itself holds no inputs to edit. */
  editItemContraCharge: 'Edit',
  editItemContraChargeLabel: 'Edit item contra charge',
  /** In the tooltip on the row's tag: what the charge is, in the terms it was priced in. */
  itemContraChargeSummary: '{quantity} {unit} × {rate}',
  /**
   * Names the tag's info control, which is a bare icon. Without it the only thing a screen reader
   * announces is the tag's own label, and the detail behind it is unreachable.
   */
  itemContraChargeDetail: 'What is being charged back',

  /**
   * The dialog the charge is set up in. Its own block because it is a screen of its own rather than
   * a cell — a line to find, then a quantity and a rate for it.
   */
  itemContraChargeDialog: {
    addTitle: 'Add item contra charge',
    editTitle: 'Edit item contra charge',
    subtitle: 'Charge a BOQ line back against {vendor}.',
    searchLabel: 'BOQ line',
    searchPlaceholder: 'Search the BOQ for a line to charge back',
    /** The picked lines, listed with the two figures each one needs. */
    linesTitle: 'Lines to charge back',
    /** The card list's only, where there is no Vendor column — labelled, since a bare name would
        read as part of the line's description above it. */
    vendorSummary: 'BOQ line from {vendor}',
    /** Column headers. `unit` heads no column — it is the quantity field's suffix. */
    columns: {
      item: 'Item',
      /** Whose BOQ line it is: the search offers every vendor's, not just the charged vendor's. */
      vendor: 'Vendor',
      vendorTooltip: 'The vendor the BOQ line belongs to',
      quantity: 'Qty',
      rate: 'Rate',
      total: 'Total',
    },
    empty: 'Find a BOQ line above to charge back.',
    quantity: 'Quantity',
    rate: 'Rate',
    total: 'Total',
    /** Not a refusal — a line can legitimately be charged back beyond what the BOQ holds. */
    exceedsBoq: 'More than the {quantity} on the BOQ line',
    /**
     * Said per field rather than once per line: each names the single thing to fix, and a line
     * missing both gets both, beside the input that fixes it.
     */
    quantityRequired: 'Enter a quantity greater than zero',
    rateRequired: 'Enter a rate greater than zero',
    remove: 'Remove line',
    cancel: 'Cancel',
    /** Names what pressing it does to the claim behind the dialog, which differs by mode. */
    add: 'Add',
    save: 'Save',
  },
  sharePercentPlaceholder: 'Percent',
  shareAmountPlaceholder: 'Amount',
  methodPlaceholder: 'Select',
  methodOptions: { account: 'Account', cashOnDelivery: 'Cash on Delivery' },

  /**
   * What the payment count is counting, on hovering it.
   *
   * Worth saying because the rows and the payments are no longer the same list: a contra charge is a
   * row that raises nothing. Without this the count reads as wrong to anyone counting the rows.
   */
  paymentsTooltip: {
    title: 'Payments raised',
    deductions:
      '{count} deduction is taken off the request rather than paid | {count} deductions are taken off the request rather than paid',
  },
  noPaymentMethod: 'No payment method set on this vendor',
  duePlaceholder: 'Select a date',
  assigneePlaceholder: 'Assign payment',
  assigneeEmpty: 'No one on this site can be assigned a payment',
  dueOnAccountTerms: "On the vendor's account terms",
  vendorUnavailable: "Couldn't load this vendor's payment methods",

  table: {
    columns: {
      /** The first column holds the vendor at the top level and its requests in the rows under it. */
      vendor: 'Vendor',
      method: 'Payment method',
      due: 'Payment due',
      assignee: 'Payment assignee',
      amount: 'Amount',
      actions: 'Actions',
    },
    /** The card list's visually-hidden marker; a column header gets the asterisk's tooltip. */
    required: 'Required',
  },
};
